<template>
  <b-modal
    id="working-schedule-calendar-modal"
    ref="workingScheduleCalendarModal"
    no-close-on-backdrop
    v-model="isModalVisible"
    size="xl"
    hide-footer
    :title="$t('breadcrumb.workingSchedule')"
    content-class="calendar-modal"
    dialog-class="modal-fullscreen"
    @shown="showModal"
    @hidden="closeModal"
  >
    <validation-observer ref="workingScheduleForm" #default="{ invalid }">
      <n-form-confirmation
        key="workingScheduleForm"
        :bypass="true"
        @submit="validateForm"
        :form="$refs.workingScheduleForm"
        :disabled="invalid"
      >
        <b-row>
          <b-col cols="12" sm="12" md="4" lg="3">
            <!-- Multi-language title input -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('label.title')"
              :rules="
                locale === appLocale
                  ? 'required|max:100'
                  : 'max:100'
              "
              style="width: 100%;"
            >
              <b-form-group
                :label="$t('label.title')"
                :state="errors.length === 0 ? null : false"
                class="mb-3"
              >
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1 mr-1">
                    <b-form-input
                      v-model="formModel.title[locale]"
                      :placeholder="$t('label.title')"
                      :state="errors.length === 0 ? null : false"
                    />
                  </div>
                  <div style="min-width: 80px">
                    <b-form-select
                      v-model="locale"
                      :options="locales"
                      class="language-select"
                    />
                  </div>
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <n-input :fields="fields" v-model="formModel" />

            <div v-if="calendarOptions.events.length > 0">
              <label>{{ $t('breadcrumb.workingTime') }}</label>
              <b-list-group class="mb-2">
                <b-list-group-item
                  v-for="event in calendarOptions.events"
                  :key="event.id"
                  class="d-flex px-1 align-items-center"
                  @click="() => handleSidebarEventClick(event)"
                >
                  <div class="dot-event-flag mr-1"></div>
                  <div>
                    <div>{{ getEventTitle(event) }}</div>
                    <small class="text-muted">{{
                      displayDate(event.rrule.dtstart)
                    }}</small>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="8" lg="9">
            <div
              class="calendar-container position-relative"
              :class="showTimeShiftDropdown && 'dropdown-active'"
              :style="{ opacity: isReady ? 1 : 0 }"
            >
              <full-calendar
                ref="fullCalendar"
                id="working-schedule-fullCalendar"
                :options="calendarOptions"
              />
            </div>
          </b-col>
        </b-row>

        <!-- Bootstrap Popover Content -->
        <b-popover
          :show="showTimeShiftDropdown"
          triggers="manual"
          target="fc-active-date"
          placement="right"
          container="working-schedule-fullCalendar"
        >
          <div style="width: 300px; min-height: 250px">
            <!-- Time Shift Dropdown form -->
            <time-shift-dropdown
              ref="timeShiftDropdown"
              :activeDate="activeDate"
              :event="activeEvent"
              @submit="saveTimeShift"
              @close="closeTimeShiftDropdown"
              @delete="deleteTimeShift"
            />
          </div>
        </b-popover>

        <!-- Footer -->
        <div class="calendar-footer d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            class="close-btn mr-2"
            @click="closeModal"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <n-button-loading
            type="submit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :loading="loading"
            :disabled="invalid || showTimeShiftDropdown || !formModel.title[locale]"
          >
            {{ $t('button.save') }}
          </n-button-loading>
        </div>
      </n-form-confirmation>
    </validation-observer>
  </b-modal>
</template>

<script>
  import {
    BModal,
    BButton,
    BFormGroup,
    BFormRadioGroup,
    BPopover,
    BCol,
    BRow,
    BListGroup,
    BListGroupItem,
    BFormInput,
    BFormSelect
  } from 'bootstrap-vue';
  import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
  import FullCalendar from '@fullcalendar/vue';
  import dayGridPlugin from '@fullcalendar/daygrid';
  import interactionPlugin from '@fullcalendar/interaction';
  import multiMonthPlugin from '@fullcalendar/multimonth';
  import rrulePlugin from '@fullcalendar/rrule';
  import kmLocale from '@fullcalendar/core/locales/km';
  import enLocale from '@fullcalendar/core/locales/en-au';
  import zhLocale from '@fullcalendar/core/locales/zh-cn';
  import viLocale from '@fullcalendar/core/locales/vi';
  import ScheduleDetailsForm from './components/ScheduleDetailsForm.vue';
  import CalendarHeader from '@/components/CalendarHeader.vue';
  import TimeShiftDropdown from './components/time-shift/TimeShiftDropdown.vue';
  import NButtonLoading from '@/components/NButtonLoading.vue';
  import NFormConfirmation from '@/components/NFormConfirmation';
  import { formatDate, uniqueId } from '@/libs/helper';

  import Ripple from 'vue-ripple-directive';
  import NInput from '@/components/NInput';
  import FormInput from './calendarModel';
  import { ValidationProvider, ValidationObserver } from 'vee-validate';

  import Repository from '@/repositories/RepositoryFactory';

  const WorkingScheduleRepository = Repository.get('workingScheduleTemplate');

  export default {
    name: 'CalendarModal',
    components: {
      BModal,
      BButton,
      BPopover,
      FeatherIcon,
      FullCalendar,
      ScheduleDetailsForm,
      CalendarHeader,
      TimeShiftDropdown,
      BFormGroup,
      BFormRadioGroup,
      NButtonLoading,
      NFormConfirmation,
      NInput,
      BCol,
      BRow,
      BListGroup,
      BListGroupItem,
      BFormInput,
      BFormSelect,
      ValidationProvider,
      ValidationObserver
    },
    props: {
      data: {
        type: Object,
        default: () => null
      }
    },
    directives: {
      Ripple
    },
    setup() {
      const fields = FormInput;
      return {
        fields
      };
    },
    data() {
      return {
        isReady: false,
        calendarApi: null,
        isFormValid: false,
        isModalVisible: false,
        locale: this.$i18n.locale.split('-')[0],
        locales: [
          { value: 'en', text: 'English' },
          { value: 'zh', text: '中文' },
          { value: 'km', text: 'ខ្មែរ' },
          { value: 'vi', text: 'Tiếng Anh' },
        ],
        calendarOptions: {
          plugins: [
            dayGridPlugin,
            interactionPlugin,
            multiMonthPlugin,
            rrulePlugin
          ],
          height: 'auto',
          initialView: 'dayGridMonth', // 'dayGridMonth',
          droppable: false,
          headerToolbar: {
            right: 'prev,today,next',
            left: 'title',
            center: 'dayGridMonth,multiMonthYear'
          },
          editable: false,
          selectable: true,
          selectMirror: true,
          dayMaxEvents: true,
          weekends: true,
          displayEventTime: false,
          firstDay: 1,
          height: '100%',
          aspectRatio: 1.35,
          nowIndicator: true,
          dateClick: this.openTimeShiftForm,
          eventClick: this.openTimeShiftForm,
          datesSet: this.resetActiveDateEl,
          views: {
            dayGridMonth: {
              dayMaxEventRows: 3,
              dayHeaderFormat: { weekday: 'short' }
            },
            multiMonthFourMonth: {
              type: 'multiMonth',
              duration: { months: 12 }
            }
          },
          locale: this.$i18n.locale,
          locales: [kmLocale, enLocale, zhLocale, viLocale],
          eventDisplay: 'list-item',
          eventBackgroundColor: '#8d5185',
          events: []
        },
        activeEvent: null,
        activeDate: null,
        showTimeShiftDropdown: false,
        formModel: {
          title: {
            en: '',
            zh: '',
            km: '',
            vi: '',
          },
          status: ''
        },
        loading: false
      };
    },
    computed: {
      appLocale() {
        return this.$i18n.locale.split('-')[0];
      }
    },
    watch: {
      '$i18n.locale'(val) {
        const [language] = val.split('-');
        this.locale = language;
      }
    },
    created() {
      this.initDate();

      this.$nextTick(() => {
        this.calendarApi = this.$refs.fullCalendar.getApi();
        setTimeout(() => {
          this.calendarApi.changeView('multiMonthYear');
          this.isReady = true;
        }, 200);
      });
    },
    mounted() {
      this.isModalVisible = true;
    },
    methods: {
      // Normalize locale-specific digits (Khmer → ASCII)
      normalizeKhmerDigits(value) {
        if (value === null || value === undefined) return value;
        const str = String(value);
        const khmerToAscii = {
          '០': '0', '១': '1', '២': '2', '៣': '3', '៤': '4',
          '៥': '5', '៦': '6', '៧': '7', '៨': '8', '៩': '9'
        };
        return str.replace(/[០១២៣៤៥៦៧៨៩]/g, d => khmerToAscii[d] || d);
      },
      // Safely parse date strings that may contain locale digits
      parseDateSafe(value) {
        if (value instanceof Date) return value;
        const normalized = this.normalizeKhmerDigits(value);
        return new Date(normalized);
      },
      // Helper method to convert string title to object
      convertTitleToObject(title) {
        if (typeof title === 'string') {
          const defaultTitle = title;
          const titleObj = {
            en: defaultTitle,
            zh: '',
            km: '',
            vi: '',
          };
          // Set the current locale's value
          const currentLocale = this.appLocale;
          if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
            titleObj[currentLocale] = defaultTitle;
          }
          return titleObj;
        }
        return title || '-';
      },
      getEventTitle(event) {
        let title = event.timeShift.titleTranslate || event.timeShift.title;
        if(typeof title === 'string') {
          return title || '-';
        }
        return title?.[this.appLocale] || title?.en || title?.zh || title?.km || title?.vi || '-';
      },
      initDate() {
        if (this.data) {
          const { title, titleTranslate, events, isActive } = this.data;
          
          // Handle title conversion for multi-language support
          const sourceTitle = titleTranslate || title;
          this.formModel.title = this.convertTitleToObject(sourceTitle);
          this.formModel.status = isActive
            ? this.$t('label.active')
            : this.$t('label.inActive');

          this.calendarOptions.events = events.map(e => {
            const {
              rrule,
              exdate,
              timeShift
            } = e;

            const { startTime, endTime } = timeShift || {};

            return {
              ...e,
              title: timeShift?.titleTranslate?.[this.appLocale] || timeShift?.titleTranslate?.en || timeShift?.titleTranslate?.zh || timeShift?.titleTranslate?.km || timeShift?.titleTranslate?.vi || e.title || '-',
              startTime,
              endTime,
              extendedRrule: { ...rrule },
              extendedExdate: [...exdate]
            };
          });
        }
      },
      validateForm() {
        this.$refs.workingScheduleForm.validate().then(success => {
          if (success) {
            this.submit();
          }
        });
      },

      async submit() {
        const _id = this.data?.id;
        const { title, status } = this.formModel;
        const events = this.calendarOptions.events.map(e => ({
          id: e.id,
          title: e.timeShift.titleTranslate?.[this.appLocale] || e.timeShift.titleTranslate?.en || e.timeShift.titleTranslate?.zh || e.timeShift.titleTranslate?.km || e.timeShift.titleTranslate?.vi || e.title ||'-',
          rrule: e.rrule,
          isCustom: e.isCustom,
          timeShift: e.timeShift.id,
          exdate: e.exdate
        }));

        const payload = {
          title,
          events,
          isActive: status == 'Active' || status?.value == 'active'
        };

        try {
          this.loading = true;

          if (_id) {
            await WorkingScheduleRepository.update(_id, payload);
          } else {
            await WorkingScheduleRepository.create(payload);
          }

          this.$emit('success');
        } catch (error) {
          // Handle error if needed
        } finally {
          this.loading = false;
        }
      },

      saveTimeShift(eventModel) {
        this.showTimeShiftDropdown = false;

        // Must add dtstart from the selected
        const { id, rrule, exdate } = eventModel;
        const activeRrule = this.activeEvent?.rrule;
        const dtstart = new Date(activeRrule?.dtstart || this.activeDate);

        const formatedRRule = {
          ...rrule,
          dtstart: this.normalizeKhmerDigits(formatDate(dtstart, 'YYYY-MM-DD')) // dtstart used YYYY-MM-DD only
        };

        const extendedExdate = exdate;

        const payload = {
          ...eventModel,
          exdate,
          title: eventModel.timeShift.titleTranslate?.[this.appLocale] || eventModel.timeShift.titleTranslate?.en || eventModel.timeShift.titleTranslate?.zh || eventModel.timeShift.titleTranslate?.km || eventModel.timeShift.titleTranslate?.vi || '-',
          rrule: formatedRRule,
          extendedRrule: formatedRRule, // add extendedRrule for easy retrieval from event instance
          extendedExdate // add extendedExdate for easy retrieval from event instance
        };

        const idx = this.calendarOptions.events.findIndex(e => e.id === id);

        if (idx > -1) {
          this.$set(this.calendarOptions.events, idx, { ...payload });
        } else {
          this.calendarOptions.events.push({
            ...payload,
            id: uniqueId() // tmp id
          });
        }
      },

      deleteTimeShift() {
        this.showTimeShiftDropdown = false;

        setTimeout(() => {
          const { id } = this.activeEvent;
          const idx = this.calendarOptions.events.findIndex(e => e.id == id);

          this.calendarOptions.events.splice(idx, 1);
        }, 100);
      },

      intiEventModel(info) {
        const { event, date, dayEl } = info;
        let el = null;

        if (event) {
          const {
            id,
            start,
            extendedProps: {
              isCustom,
              timeShift,
              extendedRrule,
              extendedExdate
            }
          } = event;

          el = document.querySelector(
            `[data-date="${this.normalizeKhmerDigits(formatDate(start, 'YYYY-MM-DD'))}"]`
          );

          const { title, startTime, endTime } = timeShift;

          this.activeEvent = {
            id,
            title,
            isCustom,
            timeShift,
            rrule: { ...extendedRrule },
            exdate: extendedExdate,
            startTime,
            endTime
          };

          this.activeDate = start;
        } else {
          this.activeEvent = null;
          this.activeDate = date;
        }

        const target = dayEl || el;
        if (target) target.id = 'fc-active-date';

        // Remove existing popover
        setTimeout(() => {
          const popover = document.querySelector('.fc-popover');
          if (popover) {
            popover.remove(); // Remove it from DOM
          }
        }, 100);
      },

      resetActiveDateEl() {
        // Remove fc-active-id from other elements
        this.$refs.fullCalendar.$el
          .querySelectorAll('#fc-active-date')
          .forEach(el => {
            el.removeAttribute('id');
          });
      },

      openTimeShiftForm(info) {
        this.resetActiveDateEl();
        this.intiEventModel(info);

        const show = () => {
          this.showTimeShiftDropdown = true;
        };

        if (this.showTimeShiftDropdown) {
          this.showTimeShiftDropdown = false;
          setTimeout(show, 250);
        } else {
          setTimeout(show, 150);
        }
      },

      showModal() {
        this.isModalVisible = true;
      },

      closeModal() {
        this.isModalVisible = false;
        this.$emit('hidden');
      },

      updateScheduleDetailsForm(data) {
        console.log('Updating schedule details form:', data);
      },

      closeTimeShiftDropdown() {
        this.showTimeShiftDropdown = false;
      },

      handleSidebarEventClick(event) {
        const { id, rrule, timeShift, exdate, isCustom } = event;
        const { dtstart } = rrule;

        this.calendarApi.gotoDate(dtstart);

        const formatedEvent = {
          id,
          start: this.parseDateSafe(dtstart),
          extendedProps: {
            isCustom,
            timeShift,
            extendedRrule: rrule,
            extendedExdate: exdate
          }
        };

        this.openTimeShiftForm({ event: formatedEvent });
      },

      displayDate(dateStr) {
        if (!dateStr) return '';
        
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        const normalized = this.normalizeKhmerDigits(dateStr);
        const date = this.parseDateSafe(normalized);
        
        return new Intl.DateTimeFormat(this.$i18n.locale, options).format(date);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .calendar-container {
    width: 100%; /* 80% of the viewport width */
    height: calc(100vh - 14rem); /* Fixed height */
    margin: auto; /* Centering */
  }
</style>

<style lang="scss">
  :root {
    --fc-button-bg-color: #8d5185;
    --fc-button-active-bg-color: #998796;
    --fc-button-hover-bg-color: #a968a0;
    --fc-button-text-color: #ffffff;
    --fc-button-active-text-color: #ffffff;
    --fc-button-border-color: gray;
    --fc-today-bg-color: #ffecce;
  }

  .calendar-modal {
    .popover {
      max-width: 500px !important;
    }
  }

  #fc-active-date {
    background-color: #e6d8e4;
  }

  .dot-event-flag {
    background-color: rgb(141, 81, 133);
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dropdown-active {
    .fc-button {
      pointer-events: none;
      opacity: 0.3;
    }

    .popover {
      width: 335px !important;
    }

    // Force popover center when out of boundary
    .popover[x-out-of-boundaries] {
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;

      .arrow {
        display: none;
      }
    }
  }

  .language-select {
    height: 100%;
  }
</style>
